import React from 'react';

interface IconProps {
  name: string;
  size?: number;
  className?: string;
}

// Map of icon names to real .ico files in public/icons
const icoFiles: Record<string, string> = {
  'explorer': '/icons/explorer.ico',
  'internet-explorer': '/icons/explorer.ico',
  'computer': '/icons/computer.ico',
  'my-computer': '/icons/computer.ico',
  'error': '/icons/error.ico',
  'search': '/icons/search.ico',
  'help': '/icons/help.ico',
  'star': '/icons/star.ico',
  'recycle-bin': '/icons/recycle-bin.ico',
  'folder': '/icons/folder.ico',
  'research': '/icons/research.ico',
};

// XP-inspired SVG icons (original parody recreations)
const icons: Record<string, React.FC<{ size: number }>> = {
  'folder-projects': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Folder back */}
      <path d="M4 12 L4 42 L44 42 L44 16 L22 16 L18 12 Z" fill="#FFD700"/>
      {/* Folder front */}
      <path d="M4 18 L4 42 L44 42 L44 18 Z" fill="url(#folderGradient)" stroke="#CC9900" strokeWidth="1"/>
      {/* Star badge */}
      <circle cx="36" cy="32" r="8" fill="#0066CC"/>
      <polygon points="36,26 37.5,30 42,30 38.5,33 40,37 36,34.5 32,37 33.5,33 30,30 34.5,30" fill="#FFD700"/>
      <defs>
        <linearGradient id="folderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFE566"/>
          <stop offset="100%" stopColor="#FFCC00"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  user: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Background card */}
      <rect x="4" y="4" width="40" height="40" rx="4" fill="url(#userCardGradient)"/>
      {/* Head */}
      <circle cx="24" cy="18" r="10" fill="#FFE0BD" stroke="#D4A574" strokeWidth="1"/>
      {/* Body */}
      <path d="M8 44 C8 32 16 28 24 28 C32 28 40 32 40 44 Z" fill="#4169E1"/>
      {/* Shirt collar */}
      <path d="M20 28 L24 34 L28 28" fill="#FFFFFF"/>
      <defs>
        <linearGradient id="userCardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E6E6FA"/>
          <stop offset="100%" stopColor="#B0C4DE"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  mail: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Envelope body */}
      <rect x="4" y="10" width="40" height="28" rx="2" fill="url(#mailGradient)" stroke="#8B7355" strokeWidth="1"/>
      {/* Envelope flap */}
      <path d="M4 10 L24 26 L44 10" fill="none" stroke="#8B7355" strokeWidth="2"/>
      {/* Envelope lines */}
      <path d="M4 38 L18 24" stroke="#8B7355" strokeWidth="1"/>
      <path d="M44 38 L30 24" stroke="#8B7355" strokeWidth="1"/>
      <defs>
        <linearGradient id="mailGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFF8DC"/>
          <stop offset="100%" stopColor="#DEB887"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  document: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Page */}
      <path d="M8 4 L32 4 L40 12 L40 44 L8 44 Z" fill="url(#docGradient)" stroke="#808080" strokeWidth="1"/>
      {/* Fold */}
      <path d="M32 4 L32 12 L40 12" fill="#C0C0C0" stroke="#808080" strokeWidth="1"/>
      {/* Lines */}
      <line x1="12" y1="18" x2="36" y2="18" stroke="#808080" strokeWidth="1"/>
      <line x1="12" y1="24" x2="36" y2="24" stroke="#808080" strokeWidth="1"/>
      <line x1="12" y1="30" x2="36" y2="30" stroke="#808080" strokeWidth="1"/>
      <line x1="12" y1="36" x2="28" y2="36" stroke="#808080" strokeWidth="1"/>
      <defs>
        <linearGradient id="docGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF"/>
          <stop offset="100%" stopColor="#F0F0F0"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  notepad: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Notepad body */}
      <rect x="8" y="4" width="32" height="40" fill="url(#notepadGradient)" stroke="#6B8E6B" strokeWidth="1"/>
      {/* Binding */}
      <rect x="8" y="4" width="6" height="40" fill="#8B0000"/>
      {/* Lines */}
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <line key={i} x1="16" y1={12 + i * 5} x2="38" y2={12 + i * 5} stroke="#87CEEB" strokeWidth="0.5"/>
      ))}
      {/* Red margin line */}
      <line x1="18" y1="4" x2="18" y2="44" stroke="#FF6B6B" strokeWidth="0.5"/>
      <defs>
        <linearGradient id="notepadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFACD"/>
          <stop offset="100%" stopColor="#F5DEB3"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  settings: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Gear */}
      <path d="M24 8 L26 8 L27 12 L30 13 L33 10 L35 12 L32 15 L33 18 L37 19 L37 21 L33 22 L32 25 L35 28 L33 30 L30 27 L27 28 L26 32 L24 32 L23 28 L20 27 L17 30 L15 28 L18 25 L17 22 L13 21 L13 19 L17 18 L18 15 L15 12 L17 10 L20 13 L23 12 Z"
            fill="url(#gearGradient)" stroke="#4A4A4A" strokeWidth="1"/>
      <circle cx="25" cy="20" r="5" fill="#E0E0E0" stroke="#4A4A4A" strokeWidth="1"/>
      {/* Control panel */}
      <rect x="8" y="34" width="32" height="10" rx="2" fill="#C0C0C0" stroke="#808080" strokeWidth="1"/>
      <circle cx="16" cy="39" r="2" fill="#00AA00"/>
      <circle cx="24" cy="39" r="2" fill="#FFAA00"/>
      <circle cx="32" cy="39" r="2" fill="#0000AA"/>
      <defs>
        <linearGradient id="gearGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A0A0A0"/>
          <stop offset="100%" stopColor="#707070"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  window: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="4" y="4" width="40" height="40" rx="2" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="4" y="4" width="40" height="8" fill="url(#windowTitleGradient)"/>
      <rect x="8" y="14" width="32" height="26" fill="#FFFFFF" stroke="#808080" strokeWidth="1"/>
      <defs>
        <linearGradient id="windowTitleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0058EE"/>
          <stop offset="100%" stopColor="#0052CC"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  start: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="20" fill="url(#startGradient)"/>
      <path d="M16 12 L16 36 L36 24 Z" fill="#FFFFFF"/>
      <defs>
        <linearGradient id="startGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#5ABA47"/>
          <stop offset="100%" stopColor="#2D8A2D"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  close: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="4" y="4" width="40" height="40" rx="4" fill="url(#closeGradient)"/>
      <path d="M14 14 L34 34 M34 14 L14 34" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
      <defs>
        <linearGradient id="closeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#E81123"/>
          <stop offset="100%" stopColor="#A00000"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  minimize: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="4" y="4" width="40" height="40" rx="4" fill="url(#minGradient)"/>
      <rect x="12" y="30" width="24" height="4" fill="#FFFFFF"/>
      <defs>
        <linearGradient id="minGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3D9AE8"/>
          <stop offset="100%" stopColor="#1D5BB6"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  maximize: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="4" y="4" width="40" height="40" rx="4" fill="url(#maxGradient)"/>
      <rect x="12" y="12" width="24" height="24" fill="none" stroke="#FFFFFF" strokeWidth="3"/>
      <rect x="12" y="12" width="24" height="6" fill="#FFFFFF"/>
      <defs>
        <linearGradient id="maxGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3D9AE8"/>
          <stop offset="100%" stopColor="#1D5BB6"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  restore: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="4" y="4" width="40" height="40" rx="4" fill="url(#restoreGradient)"/>
      <rect x="16" y="8" width="18" height="18" fill="none" stroke="#FFFFFF" strokeWidth="2"/>
      <rect x="16" y="8" width="18" height="5" fill="#FFFFFF"/>
      <rect x="10" y="18" width="18" height="18" fill="none" stroke="#FFFFFF" strokeWidth="2"/>
      <rect x="10" y="18" width="18" height="5" fill="#FFFFFF"/>
      <defs>
        <linearGradient id="restoreGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3D9AE8"/>
          <stop offset="100%" stopColor="#1D5BB6"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  volume: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path d="M8 18 L16 18 L26 8 L26 40 L16 30 L8 30 Z" fill="#808080"/>
      <path d="M30 16 C34 20 34 28 30 32" stroke="#808080" strokeWidth="2" fill="none"/>
      <path d="M34 12 C40 18 40 30 34 36" stroke="#808080" strokeWidth="2" fill="none"/>
    </svg>
  ),

  refresh: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Circular arrow */}
      <path
        d="M24 8 A16 16 0 1 1 8 24"
        stroke="#0066CC"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
      {/* Arrow head */}
      <path
        d="M8 14 L8 24 L18 24"
        stroke="#0066CC"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),

  cascade: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Back window */}
      <rect x="16" y="6" width="26" height="20" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="16" y="6" width="26" height="5" fill="#0054E3"/>
      {/* Front window */}
      <rect x="6" y="16" width="26" height="20" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="6" y="16" width="26" height="5" fill="#0054E3"/>
    </svg>
  ),

  'tile-horizontal': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Top window */}
      <rect x="6" y="6" width="36" height="16" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="6" y="6" width="36" height="4" fill="#0054E3"/>
      {/* Bottom window */}
      <rect x="6" y="26" width="36" height="16" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="6" y="26" width="36" height="4" fill="#0054E3"/>
    </svg>
  ),

  'tile-vertical': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Left window */}
      <rect x="6" y="6" width="16" height="36" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="6" y="6" width="16" height="4" fill="#0054E3"/>
      {/* Right window */}
      <rect x="26" y="6" width="16" height="36" fill="#ECE9D8" stroke="#0054E3" strokeWidth="2"/>
      <rect x="26" y="6" width="16" height="4" fill="#0054E3"/>
    </svg>
  ),

  minesweeper: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Background */}
      <rect x="4" y="4" width="40" height="40" rx="2" fill="#C0C0C0" stroke="#808080" strokeWidth="2"/>
      {/* Mine */}
      <circle cx="24" cy="24" r="10" fill="#000"/>
      {/* Spikes */}
      <line x1="24" y1="10" x2="24" y2="6" stroke="#000" strokeWidth="2"/>
      <line x1="24" y1="42" x2="24" y2="38" stroke="#000" strokeWidth="2"/>
      <line x1="10" y1="24" x2="6" y2="24" stroke="#000" strokeWidth="2"/>
      <line x1="42" y1="24" x2="38" y2="24" stroke="#000" strokeWidth="2"/>
      <line x1="14" y1="14" x2="11" y2="11" stroke="#000" strokeWidth="2"/>
      <line x1="34" y1="14" x2="37" y2="11" stroke="#000" strokeWidth="2"/>
      <line x1="14" y1="34" x2="11" y2="37" stroke="#000" strokeWidth="2"/>
      <line x1="34" y1="34" x2="37" y2="37" stroke="#000" strokeWidth="2"/>
      {/* Shine */}
      <ellipse cx="20" cy="20" rx="3" ry="2" fill="#fff"/>
    </svg>
  ),

  solitaire: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Back card */}
      <rect x="10" y="6" width="24" height="32" rx="3" fill="#1E5B1E" stroke="#0D3D0D" strokeWidth="1"/>
      <rect x="12" y="8" width="20" height="28" fill="url(#cardPattern)" rx="2"/>
      {/* Front card */}
      <rect x="14" y="10" width="24" height="32" rx="3" fill="#fff" stroke="#333" strokeWidth="1"/>
      {/* Heart */}
      <path d="M26 18 C26 16 28 14 30 14 C33 14 34 17 34 19 C34 24 26 30 26 30 C26 30 18 24 18 19 C18 17 19 14 22 14 C24 14 26 16 26 18 Z" fill="#E31937"/>
      {/* A */}
      <text x="17" y="17" fontSize="6" fontWeight="bold" fill="#E31937">A</text>
      <defs>
        <pattern id="cardPattern" patternUnits="userSpaceOnUse" width="4" height="4">
          <rect width="4" height="4" fill="#1E5B1E"/>
          <circle cx="2" cy="2" r="0.5" fill="#2A7A2A"/>
        </pattern>
      </defs>
    </svg>
  ),

  'internet-explorer': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Globe */}
      <circle cx="24" cy="24" r="18" fill="url(#ieGradient)" stroke="#0066CC" strokeWidth="2"/>
      {/* Orbit ring */}
      <ellipse cx="24" cy="24" rx="20" ry="8" fill="none" stroke="#FFD700" strokeWidth="3" transform="rotate(-30 24 24)"/>
      {/* Latitude lines */}
      <ellipse cx="24" cy="24" rx="18" ry="8" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      <ellipse cx="24" cy="24" rx="18" ry="14" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      {/* Longitude line */}
      <line x1="24" y1="6" x2="24" y2="42" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      <defs>
        <linearGradient id="ieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00A2FF"/>
          <stop offset="50%" stopColor="#0078D4"/>
          <stop offset="100%" stopColor="#002050"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  'msn-messenger': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Butterfly wings */}
      <ellipse cx="16" cy="18" rx="10" ry="12" fill="url(#msnOrange)" transform="rotate(-20 16 18)"/>
      <ellipse cx="32" cy="18" rx="10" ry="12" fill="url(#msnGreen)" transform="rotate(20 32 18)"/>
      <ellipse cx="14" cy="32" rx="8" ry="10" fill="url(#msnBlue)" transform="rotate(-10 14 32)"/>
      <ellipse cx="34" cy="32" rx="8" ry="10" fill="url(#msnRed)" transform="rotate(10 34 32)"/>
      {/* Body */}
      <ellipse cx="24" cy="26" rx="4" ry="14" fill="#333"/>
      {/* Head */}
      <circle cx="24" cy="10" r="4" fill="#333"/>
      {/* Antennae */}
      <path d="M22 7 Q20 2 18 4" stroke="#333" strokeWidth="2" fill="none"/>
      <path d="M26 7 Q28 2 30 4" stroke="#333" strokeWidth="2" fill="none"/>
      <defs>
        <linearGradient id="msnOrange" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFB800"/>
          <stop offset="100%" stopColor="#FF8C00"/>
        </linearGradient>
        <linearGradient id="msnGreen" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7FBA00"/>
          <stop offset="100%" stopColor="#4A8C00"/>
        </linearGradient>
        <linearGradient id="msnBlue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00A4EF"/>
          <stop offset="100%" stopColor="#0078D4"/>
        </linearGradient>
        <linearGradient id="msnRed" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F25022"/>
          <stop offset="100%" stopColor="#D13415"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  'command-prompt': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Window frame */}
      <rect x="4" y="4" width="40" height="40" rx="2" fill="#000" stroke="#808080" strokeWidth="2"/>
      {/* Title bar */}
      <rect x="4" y="4" width="40" height="8" fill="#000080"/>
      {/* Title bar buttons */}
      <rect x="32" y="6" width="4" height="4" fill="#C0C0C0"/>
      <rect x="37" y="6" width="4" height="4" fill="#C0C0C0"/>
      {/* Command prompt text */}
      <text x="8" y="20" fontSize="6" fill="#C0C0C0" fontFamily="monospace">C:\&gt;_</text>
      {/* Prompt cursor */}
      <rect x="8" y="26" width="32" height="1" fill="#C0C0C0"/>
      <text x="8" y="34" fontSize="5" fill="#C0C0C0" fontFamily="monospace">Microsoft(R)</text>
      <text x="8" y="40" fontSize="5" fill="#C0C0C0" fontFamily="monospace">Windows XP</text>
    </svg>
  ),

  minecraft: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={{ imageRendering: 'pixelated' }}>
      {/* Isometric grass block */}
      {/* Top face - grass */}
      <polygon points="16,2 30,9 16,16 2,9" fill="#7CB342"/>
      {/* Top face grass texture pixels */}
      <rect x="10" y="5" width="2" height="2" fill="#8BC34A"/>
      <rect x="14" y="7" width="2" height="2" fill="#8BC34A"/>
      <rect x="18" y="4" width="2" height="2" fill="#8BC34A"/>
      <rect x="12" y="9" width="2" height="2" fill="#689F38"/>
      <rect x="16" y="6" width="2" height="2" fill="#689F38"/>
      <rect x="20" y="8" width="2" height="2" fill="#558B2F"/>
      <rect x="8" y="7" width="2" height="1" fill="#558B2F"/>

      {/* Left face - dirt with grass edge */}
      <polygon points="2,9 16,16 16,30 2,23" fill="#8D6E63"/>
      {/* Grass edge on left face */}
      <polygon points="2,9 16,16 16,18 2,11" fill="#6B8E23"/>
      {/* Left face dirt texture */}
      <rect x="4" y="14" width="2" height="2" fill="#6D4C41"/>
      <rect x="8" y="18" width="2" height="2" fill="#6D4C41"/>
      <rect x="6" y="22" width="2" height="2" fill="#6D4C41"/>
      <rect x="10" y="16" width="2" height="2" fill="#A1887F"/>
      <rect x="4" y="20" width="2" height="2" fill="#A1887F"/>
      <rect x="12" y="22" width="2" height="2" fill="#5D4037"/>

      {/* Right face - dirt with grass edge */}
      <polygon points="16,16 30,9 30,23 16,30" fill="#795548"/>
      {/* Grass edge on right face */}
      <polygon points="16,16 30,9 30,11 16,18" fill="#558B2F"/>
      {/* Right face dirt texture */}
      <rect x="20" y="14" width="2" height="2" fill="#5D4037"/>
      <rect x="24" y="18" width="2" height="2" fill="#5D4037"/>
      <rect x="18" y="20" width="2" height="2" fill="#5D4037"/>
      <rect x="22" y="16" width="2" height="2" fill="#8D6E63"/>
      <rect x="26" y="20" width="2" height="2" fill="#8D6E63"/>
      <rect x="20" y="24" width="2" height="2" fill="#4E342E"/>

      {/* Block outlines */}
      <polygon points="16,2 30,9 16,16 2,9" fill="none" stroke="#3E2723" strokeWidth="0.5"/>
      <polygon points="2,9 16,16 16,30 2,23" fill="none" stroke="#3E2723" strokeWidth="0.5"/>
      <polygon points="16,16 30,9 30,23 16,30" fill="none" stroke="#3E2723" strokeWidth="0.5"/>
    </svg>
  ),

  research: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Folder back */}
      <path d="M4 14 L4 42 L44 42 L44 18 L22 18 L18 14 Z" fill="#8B4513"/>
      {/* Folder front */}
      <path d="M4 20 L4 42 L44 42 L44 20 Z" fill="url(#researchFolderGradient)" stroke="#6B3410" strokeWidth="1"/>
      {/* Paper stack */}
      <rect x="10" y="24" width="20" height="14" rx="1" fill="#FFFFFF" stroke="#C0C0C0" strokeWidth="0.5"/>
      <rect x="12" y="22" width="20" height="14" rx="1" fill="#FFFFFF" stroke="#C0C0C0" strokeWidth="0.5"/>
      <rect x="14" y="20" width="20" height="14" rx="1" fill="#FFFFFF" stroke="#808080" strokeWidth="0.5"/>
      {/* Lines on paper */}
      <line x1="17" y1="24" x2="31" y2="24" stroke="#0054E3" strokeWidth="0.5"/>
      <line x1="17" y1="27" x2="31" y2="27" stroke="#808080" strokeWidth="0.5"/>
      <line x1="17" y1="30" x2="26" y2="30" stroke="#808080" strokeWidth="0.5"/>
      {/* Magnifying glass */}
      <circle cx="36" cy="30" r="6" fill="none" stroke="#0054E3" strokeWidth="2"/>
      <circle cx="36" cy="30" r="4" fill="rgba(0, 84, 227, 0.1)"/>
      <line x1="40" y1="34" x2="44" y2="38" stroke="#0054E3" strokeWidth="3" strokeLinecap="round"/>
      {/* Graduation cap accent */}
      <path d="M8 10 L16 6 L24 10 L16 14 Z" fill="#1A1A1A"/>
      <rect x="14" y="10" width="4" height="4" fill="#1A1A1A"/>
      <circle cx="16" cy="7" r="1" fill="#FFD700"/>
      <defs>
        <linearGradient id="researchFolderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D2691E"/>
          <stop offset="100%" stopColor="#8B4513"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  pdf: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Page */}
      <path d="M8 4 L32 4 L40 12 L40 44 L8 44 Z" fill="url(#pdfGradient)" stroke="#CC0000" strokeWidth="1"/>
      {/* Fold */}
      <path d="M32 4 L32 12 L40 12" fill="#FFCCCC" stroke="#CC0000" strokeWidth="1"/>
      {/* PDF logo area */}
      <rect x="12" y="20" width="24" height="16" rx="2" fill="#CC0000"/>
      <text x="24" y="32" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#FFFFFF" fontFamily="Arial, sans-serif">PDF</text>
      {/* Lines */}
      <line x1="12" y1="40" x2="36" y2="40" stroke="#CC0000" strokeWidth="0.5" opacity="0.5"/>
      <defs>
        <linearGradient id="pdfGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF"/>
          <stop offset="100%" stopColor="#FFE0E0"/>
        </linearGradient>
      </defs>
    </svg>
  ),

  youtube: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Red rounded rectangle background */}
      <rect x="4" y="10" width="40" height="28" rx="8" fill="#FF0000"/>
      {/* White play button */}
      <path d="M20 16 L20 32 L34 24 Z" fill="#FFFFFF"/>
    </svg>
  ),

  presentation: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* Screen */}
      <rect x="4" y="8" width="40" height="28" rx="2" fill="url(#presentationGradient)" stroke="#0054E3" strokeWidth="2"/>
      {/* Screen content */}
      <rect x="8" y="12" width="32" height="20" fill="#FFFFFF"/>
      {/* Chart bars */}
      <rect x="12" y="22" width="5" height="8" fill="#4CAF50"/>
      <rect x="19" y="18" width="5" height="12" fill="#2196F3"/>
      <rect x="26" y="14" width="5" height="16" fill="#FF9800"/>
      <rect x="33" y="20" width="5" height="10" fill="#9C27B0"/>
      {/* Stand */}
      <rect x="22" y="36" width="4" height="6" fill="#808080"/>
      <rect x="16" y="42" width="16" height="2" rx="1" fill="#606060"/>
      <defs>
        <linearGradient id="presentationGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#E8F0FE"/>
          <stop offset="100%" stopColor="#B0C4DE"/>
        </linearGradient>
      </defs>
    </svg>
  ),
};

export const Icon: React.FC<IconProps> = ({ name, size = 32, className }) => {
  // Check if we have a real .ico file for this icon
  const icoPath = icoFiles[name];

  if (icoPath) {
    return (
      <img
        src={icoPath}
        alt={name}
        width={size}
        height={size}
        className={className}
        style={{
          display: 'inline-block',
          imageRendering: size <= 32 ? 'auto' : 'auto',
        }}
      />
    );
  }

  // Fall back to SVG icon
  const IconComponent = icons[name];

  if (!IconComponent) {
    // Fallback icon
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" className={className}>
        <rect x="4" y="4" width="40" height="40" rx="4" fill="#C0C0C0" stroke="#808080" strokeWidth="2"/>
        <text x="24" y="28" textAnchor="middle" fontSize="16" fill="#808080">?</text>
      </svg>
    );
  }

  return (
    <span className={className} style={{ display: 'inline-flex' }}>
      <IconComponent size={size} />
    </span>
  );
};

export default Icon;
